import React, { useState } from "react";

import { Button, TextField, Popover } from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import SaveIcon from "@material-ui/icons/Save";

import List from "../../components/layout/list";

import { RefundDialogProps, ReceiptItemRefundEditorProps } from "./types";
import useStyles from "./styles";
import { parseNumber } from "../../utils";

const RefundDialog = ({
  receiptItems,
  onReceiptItemChanged
}: RefundDialogProps) => {
  const styles = useStyles();

  return (
    <List
      items={receiptItems}
      renderItem={receiptItem => (
        <ReceiptItemRefundEditor
          receiptItem={receiptItem}
          onReceiptItemChanged={onReceiptItemChanged}
        />
      )}
      keyExtractor={receiptItem => receiptItem.id}
    />
  );
};

export default RefundDialog;

const ReceiptItemRefundEditor = ({
  receiptItem,
  onReceiptItemChanged
}: ReceiptItemRefundEditorProps) => {
  const styles = useStyles();

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [showConfirmPopover, setShowConfirmPopover] = useState<boolean>(false);
  const [refundQuantity, setRefundQuantity] = useState<number>(
    receiptItem.refundQuantity ?? 0
  );

  const [popoverAnchor, setPopoverAnchor] = useState<HTMLButtonElement | null>(
    null
  );

  const onConfirm = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isEditing) {
      setShowConfirmPopover(true);
    } else {
      setPopoverAnchor(event.currentTarget);
      setIsEditing(true);
    }
  };

  const isValid = () => refundQuantity !== receiptItem.refundQuantity;

  const popoverId = Math.random().toString(36).substring(7);

  return (
    <div className={styles.refundDialogContainer}>
      <div className={styles.refundItemHeader}>
        {receiptItem.productName}

        <Button
          aria-describedby={popoverId}
          color="primary"
          onClick={onConfirm}
          disabled={isEditing && !isValid()}
        >
          {isEditing ? <SaveIcon /> : <EditIcon />}
        </Button>
        <Popover
          id={popoverId}
          anchorEl={popoverAnchor}
          open={showConfirmPopover}
        >
          <div className={styles.refundPopover}>
            <h3>{t("common.messages.areYouSure")}</h3>

            <div className={styles.refundConfirmButtons}>
              <Button
                color="primary"
                variant="contained"
                onClick={() => {
                  onReceiptItemChanged({ ...receiptItem, refundQuantity });
                  setIsEditing(false);
                  setShowConfirmPopover(false);
                }}
                className={styles.refundSaveButton}
              >
                Save
              </Button>
              <Button
                color="secondary"
                variant="outlined"
                onClick={() => {
                  setRefundQuantity(receiptItem.refundQuantity ?? 0);
                  setIsEditing(false);
                  setShowConfirmPopover(false);
                }}
                className={styles.refundCancelButton}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Popover>
      </div>

      <div className={styles.refundItemQuantityContainer}>
        <div className={styles.refundItemQuantity}>
          <label>{`Quantity: ${receiptItem.quantity}`}</label>
        </div>
        <div className={styles.refundItemRefundQuantity}>
          <span className={styles.refundQuantityLabel}>
            {t("common.formFields.refundQuantity")}:
          </span>
          {isEditing ? (
            <TextField
              value={refundQuantity}
              type="number"
              inputProps={{ min: 0, max: receiptItem.quantity }}
              onChange={({ target: { value } }) =>
                setRefundQuantity(parseNumber(value) ?? 0)
              }
              className={styles.refundQuantityValue}
            />
          ) : (
            <label>{refundQuantity}</label>
          )}
        </div>
      </div>
    </div>
  );
};
